<template>
  <section class="settings-dropdown">
    <EScrollableContent class="settings-dropdown__content" :scroll-shadow="false">
      <template #fixed>
        <header
          v-if="currentSearchRegion"
          class="settings-dropdown__selected"
          data-test-id="settings-dropdown-current-region"
        >
          {{ currentSearchRegion }}
          <EIcon
            class="settings-dropdown__selected-icon"
            icon="check"
            size="s"
          />
        </header>
      </template>
      <template #scrollable>
        <EList class="settings-dropdown__list" role="none">
          <EListItem
            v-for="searchRegion in searchRegions"
            :key="searchRegion.value"
            :data-test-id="`settings-dropdown-region-${searchRegion.value}`"
            class="settings-dropdown__item"
            role="menuitem"
            @click="updateSearchRegion(searchRegion.value)"
            @keyup.enter.prevent.stop="updateSearchRegion(searchRegion.value)"
          >
            {{ localizeSearchRegion(searchRegion) }}
          </EListItem>
        </EList>
      </template>
    </EScrollableContent>
    <footer class="settings-dropdown__footer">
      <a
        href="/settings"
        class="settings-dropdown__more-link"
        role="menuitem"
        rel="noopener"
        data-test-id="settings-dropdown-link"
      >
        {{ this.$t('common.header.dropdowns.settings.more') }}
      </a>
    </footer>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import EcosiaUser from '@ecosia/ecosia-user';

import searchRegions from '~~/common/js/universal/markets';
import { localiseMarket } from '~~/common/js/universal/intl';
import EIcon from '~~/common/vue/components/icon';
import EList from '~~/common/vue/components/list';
import EListItem from '~~/common/vue/components/list-item';
import EScrollableContent from '~~/common/vue/components/scrollable-content';

/**
 * Content for the Settings dropdown in the main search navigation
 */
export default {
  name: 'ESettingsDropdown',
  components: {
    EIcon,
    EList,
    EListItem,
    EScrollableContent,
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', ['bingMarketCode']),
    searchRegions() {
      return searchRegions;
    },
    currentSearchRegion() {
      const region = this.searchRegions.find(({ value }) => value === this.bingMarketCode);
      if (region) {
        return this.localizeSearchRegion(region);
      }
      return null;
    },
  },
  methods: {
    updateSearchRegion(searchRegion) {
      const user = new EcosiaUser({
        cookieString: document.cookie,
        userAgent: navigator.userAgent,
        acceptLanguage: navigator.language,
      });

      user.set('bingMarketCode', searchRegion);
      user.set('customSettings', 1);

      const url = new URL(window.location.href);
      url.searchParams.delete('p');
      window.location.assign(url.toString());
      // it could happen that the new URL is the same as the old one
      // so we also have to reload
      window.location.reload();
    },
    localizeSearchRegion({ value, label, languageInLabel }) {
      return localiseMarket(this.locale, value, languageInLabel) ?? label;
    },
  },
};
</script>

<style lang="scss" scoped>
$settings-list-max-height: 300px;

.settings-dropdown__content {
  height: $settings-list-max-height;
  padding: 0;
  position: relative;
}

.settings-dropdown__footer {
  @include divider('top');

  font-size: $font-m;
  max-height: $space-3l;
  padding: $space-s $space-m;
  text-align: center;

  @include desktop {
    font-size: $font-s;
  }
}

.settings-dropdown__selected {
  @include divider('bottom');

  font-size: $font-m;
  font-weight: $font-weight-1l;
  height: $space-3l;
  margin-left: $space-s;
  margin-right: $space-s;
  padding: $space-s $space-s $space-s $space-1s;
  position: relative;

  @include desktop {
    font-size: $font-s;
    padding-left: $space-s;
  }
}

.settings-dropdown__item {
  padding-left: $space-m;
  min-height: $space-3l;

  @include desktop {
    font-size: $font-s;
    padding-left: $space-l;
  }

  &:focus {
    @extend %keyboard-nav-focus;

    background: none;
  }
}

.settings-dropdown__more-link {
  color: $blue-50;

  &:active,
  &:hover,
  &:focus {
    text-decoration: underline;
  }
}

.settings-dropdown__selected-icon {
  position: absolute;
  top: 50%;
  right: $space-s;
  transform: translateY(-50%);
  pointer-events: none;
}
</style>
